import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Save, Printer, List as ListIcon, DoorClosed, UserMinus } from "lucide-react";
import DateInput from "./DateInput";
import { WinButton } from "./WinComponents";
import HomologOrphelinesModal from "./HomologOrphelinesModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal Homologation — parité stricte VB.NET `FrmHomolog.vb` (1464 lignes).
 * 7 blocs : Mandat, Requérant, Corequérant, Refus×2, Paiement, Info paiement co-req.
 *
 * Le formulaire s'ouvre depuis le menu principal "Homologation → Ouvrir"
 * (parité `mnuOuvrirHomolog_Click` MDIParent2.vb:4072).
 */

const EMPTY_FORM = {
  noRef: "", NomAvo: "", PrenomAvo: "", NoAvo: "",
  // Requérant (hydraté)
  nom_req: "", prenom_req: "", dateNaiss_req: "",
  noReg_req: "", noBur_req: "", noSeq_req: "", natures_req: "",
  formRequis_req: "0", renoncement_req: "0", retrait_req: "0",
  gratuit_req: "1", paye_req: "0", emetteurAttest_req: "1",
  refus42_req: "0", refus47_req: "0",
  // Corequérant
  nom_req2: "", prenom_req2: "", dateNaiss_req2: "",
  noReg_req2: "", noBur_req2: "", noSeq_req2: "", natures_req2: "",
  formRequis_req2: "0", renoncement_req2: "0", retrait_req2: "0",
  gratuit_req2: "0", paye_req2: "0", emetteurAttest_req2: "0",
  refus42_req2: "0", refus47_req2: "0",
  // Paiement
  NoPaiement: "", date_paiement: "", type_paiement: "", montant: 0,
  EmailBurReq2: "", NoDossierCour: "",
  homolog_id: "", code_suivi: "",
};

// Liste des 16 champs "flag" (VARCHAR(1) '0'/'1' en DB legacy). En prod SQL
// Server, pyodbc peut retourner ces valeurs en boolean (True/False) ou en
// int (1/0) selon la config ODBC — on normalise systématiquement en string
// "1"/"0" pour que les checkbox `checked={form.xxx === "1"}` marchent partout.
const FLAG_FIELDS = [
  "formRequis_req", "renoncement_req", "retrait_req", "gratuit_req",
  "paye_req", "emetteurAttest_req", "refus42_req", "refus47_req",
  "formRequis_req2", "renoncement_req2", "retrait_req2", "gratuit_req2",
  "paye_req2", "emetteurAttest_req2", "refus42_req2", "refus47_req2",
];

const _normalizeFlags = (data) => {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  for (const k of FLAG_FIELDS) {
    if (k in out) {
      const v = out[k];
      // Coerce boolean/int/string vers "1" (truthy '1' ou True/1) sinon "0"
      out[k] = (v === true || v === 1 || v === "1" || v === "True"
                || v === "true") ? "1" : "0";
    }
  }
  return out;
};

// Extrait le code 2-chars depuis un champ legacy qui peut contenir soit
// juste le code ("90") soit code + libellé ("90 Côte-Nord", "01 - Sept-Îles").
// Legacy prod data conserve parfois les 2 en concaténation → on normalise
// pour que les dropdowns Région/Bureau retrouvent leur `<option value="XX">`.
const _extractCode2 = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).trim();
  if (!s) return "";
  return s.substring(0, 2);
};
const _CODE_FIELDS = ["noReg_req", "noBur_req", "noReg_req2", "noBur_req2"];
const _normalizeCodes = (data) => {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  for (const k of _CODE_FIELDS) {
    if (k in out && out[k] != null) out[k] = _extractCode2(out[k]);
  }
  return out;
};

const HomologModal = ({ isOpen, onClose, formData, currentDossier,
                       showPopup }) => {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [rowId, setRowId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regions, setRegions] = useState([]);
  const [bureauxByRegion, setBureauxByRegion] = useState([]);
  const [bureauxReq, setBureauxReq] = useState([]);
  const [typesPaiement, setTypesPaiement] = useState([]);
  const [tarifInfo, setTarifInfo] = useState(null);
  const [showOrphelines, setShowOrphelines] = useState(false);
  const [showAvisNouvelle, setShowAvisNouvelle] = useState(false);
  const initializedRef = useRef(false);

  const setField = (k, v) => { setForm(p => ({ ...p, [k]: v })); setIsDirty(true); };
  const setFlag  = (k, checked) => setField(k, checked ? "1" : "0");

  // ---------- Chargement des référentiels + hydratation initiale ----------
  const hydrateFromDaj = useCallback(() => {
    const seq9 = (formData.no_sequence || currentDossier?.no_seq_daj || "")
                   .toString().substring(0, 9);
    const noReg = formData.region || currentDossier?.no_reg || "";
    const noBur = formData.bureau || currentDossier?.no_bur || "";
    const nature = `${formData.service?.code_nature || ""} - `
                 + `${formData.service?.nature || ""}`;
    setForm(p => ({
      ...p,
      nom_req: (formData.nom || "").trim(),
      prenom_req: (formData.prenom || "").trim(),
      dateNaiss_req: (formData.date_naissance || "").substring(0, 10),
      noReg_req: noReg,
      noBur_req: noBur,
      noSeq_req: seq9,
      natures_req: nature,
      natures_req2: nature,  // legacy : pré-rempli avec la même nature
      NoDossierCour: (formData.service?.no_dossier_cour ||
                      formData.no_dossier_cour || "").trim(),
      noRef: (formData.no_reference || "").trim(),
      // Gratuit par défaut si `chkGratuit` du parent (mock : true si contrib_max=0)
      gratuit_req: (formData.contribution_max || 0) > 0 ? "0" : "1",
    }));
  }, [formData, currentDossier]);

  const load = useCallback(async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    try {
      // Référentiels en parallèle
      const [reg, tp, tarif] = await Promise.all([
        axios.get(`${API}/reference/regions`),
        axios.get(`${API}/reference/type-paiement`),
        axios.get(`${API}/reference/homolog-tarif`),
      ]);
      // Normalise les codes (défense contre padding CHAR(N) en prod SQL Server)
      const regClean = (reg.data || []).map(r => ({
        ...r, code: (r.code || "").toString().trim(),
      }));
      setRegions(regClean);
      setTypesPaiement(tp.data || []);
      setTarifInfo(tarif.data || null);

      // Hydratation initiale depuis le dossier ouvert
      hydrateFromDaj();

      // Cherche une homolog existante
      const seq9 = (formData.no_sequence || currentDossier?.no_seq_daj || "")
                     .toString().substring(0, 9);
      const noReg = formData.region || currentDossier?.no_reg || "";
      const noBur = formData.bureau || currentDossier?.no_bur || "";
      const nomReq = (formData.nom || "").trim();
      const prenomReq = (formData.prenom || "").trim();
      const dnReq = (formData.date_naissance || "").substring(0, 10);
      const { data: existing } = await axios.get(`${API}/homolog/by-dossier`, {
        params: { noreg: noReg, nobur: noBur, noseq: seq9,
                  nom: nomReq, prenom: prenomReq, date_naiss: dnReq },
      });
      if (existing?.exists && existing.data) {
        // Normalise les codes région/bureau (peuvent être stockés en legacy
        // comme "90 Côte-Nord" au lieu de "90") + flags "1"/"0".
        const cleaned = _normalizeCodes(_normalizeFlags(existing.data));
        // ⚠ Si la ligne DB est orpheline (noSeq_req vide) mais qu'on l'ouvre
        // depuis un dossier ayant un noSeq, on renseigne noSeq_req avec
        // celui du dossier — on est en train de "matérialiser" l'orpheline
        // et de la lier au dossier officiel du requérant.
        if ((!cleaned.noSeq_req || !cleaned.noSeq_req.trim()) && seq9) {
          cleaned.noSeq_req = seq9;
          setIsDirty(true);  // signale qu'un save est requis
        }
        setForm(p => ({ ...p, ...cleaned }));
        setRowId(existing.data.row_id);
        // Pré-calcul montant si non gratuit
        if (existing.data.gratuit_req !== "1" && tarif.data?.montant_total) {
          setForm(p => ({ ...p, montant: tarif.data.montant_total }));
        }
      } else {
        // Aucune homolog trouvée → avis + ouverture liste orphelines
        setShowAvisNouvelle(true);
        if (tarif.data?.montant_total) {
          setForm(p => ({
            ...p,
            montant: p.gratuit_req === "1" ? 0 : tarif.data.montant_total,
          }));
        }
      }
    } catch (e) {
      showPopup?.("Erreur lors du chargement de l'homologation : "
                  + (e.response?.data?.detail || e.message),
                  "error", "Erreur");
    }
  }, [formData, currentDossier, hydrateFromDaj, showPopup]);

  useEffect(() => {
    if (isOpen) {
      initializedRef.current = false;
      setForm({ ...EMPTY_FORM });
      setRowId(null);
      load();
    } else {
      initializedRef.current = false;
      setIsDirty(false);
    }
    // ⚠ NE PAS ajouter `load` en dépendance : la fonction est recréée à
    // chaque re-render du parent (via useCallback → formData/currentDossier),
    // ce qui provoquerait un `setRowId(null)` en cours de session et
    // ferait perdre le rowId lors du save → doublon dans la table homolog.
  }, [isOpen]);

  // Cascade Région → Bureau (parité `LoadBureauxByDt`)
  useEffect(() => {
    if (!form.noReg_req2) { setBureauxByRegion([]); return; }
    axios.get(`${API}/reference/bureaux-by-region`,
              { params: { region: form.noReg_req2.substring(0, 2) } })
         .then(r => {
           // Normalise codes (défense contre padding CHAR(N) prod SQL Server)
           const cleaned = (r.data || []).map(b => ({
             ...b, code: (b.code || "").toString().trim(),
           }));
           setBureauxByRegion(cleaned);
         })
         .catch(() => setBureauxByRegion([]));
  }, [form.noReg_req2]);

  // Fetch bureaux du requérant (readonly) → pour enrichir affichage
  // "01 - Sept-Îles" au lieu de "01" seul, cohérent avec le bloc co-req.
  useEffect(() => {
    if (!form.noReg_req) { setBureauxReq([]); return; }
    axios.get(`${API}/reference/bureaux-by-region`,
              { params: { region: form.noReg_req.substring(0, 2) } })
         .then(r => {
           const cleaned = (r.data || []).map(b => ({
             ...b, code: (b.code || "").toString().trim(),
           }));
           setBureauxReq(cleaned);
         })
         .catch(() => setBureauxReq([]));
  }, [form.noReg_req]);

  // Auto : gratuit ↔ montant
  useEffect(() => {
    if (form.gratuit_req === "1") {
      setForm(p => ({ ...p, montant: 0, paye_req: "0" }));
    } else if (tarifInfo?.montant_total && !form.montant) {
      setForm(p => ({ ...p, montant: tarifInfo.montant_total }));
    }
      {/* Auto : gratuit_req persistant */}
  }, [form.gratuit_req]);

  // ---------- Save ----------
  const doSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Force tous les flags à "1"/"0" strings (parité DB VARCHAR(1))
      for (const k of FLAG_FIELDS) {
        payload[k] = payload[k] === "1" ? "1" : "0";
      }
      // Cast montant en float
      payload.montant = parseFloat(payload.montant) || 0;
      // ⚠ CRITIQUE — Si `rowId` est connu (ligne chargée depuis
      // by-dossier ou orphelines), on l'envoie pour forcer un UPDATE
      // STRICT de cette ligne. Sinon le backend risque, en présence de
      // doublons historiques prod avec le même `homolog_id`, de matcher
      // la mauvaise ligne et créer de nouveaux doublons.
      if (rowId) payload.row_id = rowId;
      const { data } = await axios.post(`${API}/homolog`, payload);
      if (data?.success) {
        setRowId(data.row_id);
        setForm(p => ({ ...p, ..._normalizeFlags(data.data) }));
        setIsDirty(false);
        showPopup?.("Homologation enregistrée avec succès.", "info", "Homologation");
      }
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      showPopup?.(`Validation Homologation :\n\n${detail}`, "warning", "Validation");
    } finally {
      setSaving(false);
    }
  };

  const doClose = () => {
    if (isDirty && !window.confirm("Voulez-vous enregistrer les modifications avant de fermer ?")) {
      // Ne sauve pas
    } else if (isDirty) {
      doSave().then(() => onClose?.());
      return;
    }
    onClose?.();
  };

  const doEnleverCoreq = async () => {
    if (!rowId) {
      showPopup?.("Enregistrez d'abord l'homologation avant d'enlever le corequérant.",
                  "warning", "Homologation");
      return;
    }
    if (!window.confirm("Voulez-vous vraiment enlever le corequérant de cette "
                        + "homologation ? Le corequérant sera remis comme "
                        + "demande orpheline dans son bureau d'origine.")) return;
    try {
      const { data } = await axios.post(
        `${API}/homolog/${rowId}/enlever-coreq`);
      if (data?.success) {
        setRowId(data.row_id);  // ← défensif : garantit qu'un futur save = PUT
        setForm(p => ({ ...p, ..._normalizeFlags(data.data),
          // Force les champs vides côté state (au cas où backend renverrait null)
          nom_req2: "", prenom_req2: "", dateNaiss_req2: "",
          noReg_req2: "", noBur_req2: "", noSeq_req2: "", natures_req2: "",
          formRequis_req2: "0", renoncement_req2: "0", retrait_req2: "0",
          gratuit_req2: "0", paye_req2: "0",
          refus42_req2: "0", refus47_req2: "0", emetteurAttest_req2: "0",
          // Info paiement / cour du co-req (No. cause + courriel)
          EmailBurReq2: "", NoDossierCour: "",
          emetteurAttest_req: "1",
        }));
        setIsDirty(false);
        const msg = data.orpheline_row_id
          ? "Corequérant enlevé et remis en demande orpheline dans son bureau."
          : "Corequérant enlevé avec succès.";
        showPopup?.(msg, "info", "Homologation");
      }
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 409) {
        showPopup?.(detail, "warning", "Action bloquée");
      } else {
        showPopup?.(`Erreur : ${detail}`, "error", "Erreur");
      }
    }
  };

  if (!isOpen) return null;
  const noSeqReq2Set = !!(form.noSeq_req2 || "").trim();
  // Styles compactes pour fit dans 900px de hauteur sans scroll
  const inp = { border: "1px solid #808080", padding: "2px 5px", fontSize: 14,
                fontFamily: "inherit", width: "100%", background: "#fff", height: 26 };
  const inpRO = { ...inp, background: "#e0e0e0", color: "#333" };
  const lbl = { fontSize: 13, fontWeight: 600, color: "#000",
                whiteSpace: "nowrap", alignSelf: "center" };
  // Grille inline label:input pour chaque bloc requérant/corequérant
  const row = { display: "grid", gridTemplateColumns: "95px 1fr",
                gap: 4, alignItems: "center", marginBottom: 3 };
  const sectionTitle = { background: "linear-gradient(to bottom, #dbe6f0, #b8ccd8)",
                         color: "#111", padding: "3px 8px", fontSize: 14,
                         fontWeight: 700, borderBottom: "1px solid #808080" };
  const fs = { border: "1px solid #808080", padding: 0, background: "#f5f5f5",
               display: "flex", flexDirection: "column" };
  const fsBody = { padding: 6 };
  const chkLbl = { fontSize: 13, display: "flex", alignItems: "center", gap: 3 };

  return (
    <div className="popup-overlay" data-testid="homolog-modal">
      <div onClick={e => e.stopPropagation()} style={{
        background: "#dbe6f0", border: "1px solid #808080",
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        width: "min(1180px, 98vw)", maxHeight: "94vh",
        display: "flex", flexDirection: "column",
      }}>
        {/* Title bar style Windows */}
        <div style={{
          background: "linear-gradient(to bottom, #4a7ba8, #234f78)",
          padding: "5px 10px", fontSize: 13, fontWeight: 600, color: "#fff",
          borderBottom: "1px solid #808080",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span data-testid="homolog-titre">
            Homologation &nbsp;·&nbsp; {form.nom_req || "-"}, {form.prenom_req || "-"}
            &nbsp;·&nbsp; {form.noSeq_req || "-"}
          </span>
          <button onClick={doClose} data-testid="homolog-close"
                  style={{ background: "transparent", border: "none", color: "#fff",
                           cursor: "pointer", fontSize: 16, fontWeight: 700,
                           padding: "0 4px" }}>×</button>
        </div>

        {/* Toolbar Windows */}
        <div className="win-toolbar" style={{ padding: "6px 10px", gap: 6 }}>
          <WinButton IconComponent={Save} onClick={doSave} disabled={saving || !isDirty}
                     data-testid="homolog-save" title="Enregistrer">Enregistrer</WinButton>
          <WinButton IconComponent={Printer} disabled data-testid="homolog-print"
                     title="Imprimer (Phase 4)">Imprimer</WinButton>
          <WinButton IconComponent={ListIcon}
                     onClick={() => setShowOrphelines(true)}
                     disabled={noSeqReq2Set}
                     data-testid="homolog-liste"
                     title="Liste des demandes orphelines">Liste</WinButton>
          {/* Enlever corequérant : uniquement si un coreq est présent
              (noSeqReq2Set) ET si la ligne homolog existe (rowId) ET si
              l'attestation n'a pas encore été émise (code_suivi != "4",
              vérifié aussi côté backend pour sécurité). */}
          <WinButton IconComponent={UserMinus}
                     onClick={doEnleverCoreq}
                     disabled={!rowId || !noSeqReq2Set || form.code_suivi === "4"}
                     data-testid="homolog-enlever-coreq"
                     title={
                       form.code_suivi === "4"
                         ? "Impossible : attestation déjà émise"
                         : !noSeqReq2Set
                         ? "Aucun corequérant à enlever"
                         : "Enlever le corequérant (efface toutes ses informations)"
                     }>
            Enlever co-req.
          </WinButton>
          <WinButton IconComponent={DoorClosed} onClick={doClose}
                     data-testid="homolog-fermer" title="Fermer">Fermer</WinButton>
        </div>

        {/* Body — layout compact fixed */}
        <div style={{ padding: 8, overflow: "auto", flex: 1 }}>
          {showAvisNouvelle && (
            <div data-testid="avis-nouvelle-homolog"
                 style={{ background: "#dbeafe", border: "1px solid #3b82f6",
                          padding: 6, marginBottom: 8, fontSize: 13,
                          display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 16 }}>ℹ️</span>
              <div style={{ flex: 1 }}>
                <b>Nouvelle Homologation</b> — vérifiez la Liste des demandes
                orphelines si le corequérant s{"'"}est déjà présenté à son bureau.
              </div>
              <button onClick={() => setShowAvisNouvelle(false)}
                      data-testid="btn-avis-ok"
                      style={{ padding: "2px 10px", fontSize: 13 }}>OK</button>
            </div>
          )}

          {/* Info mandat — 1 seule ligne */}
          <div style={fs}>
            <div style={sectionTitle}>Informations sur le mandat</div>
            <div style={{ ...fsBody, display: "grid",
                          gridTemplateColumns: "90px 1fr 60px 2fr", gap: 6,
                          alignItems: "center" }}>
              <label style={lbl}>No. référence</label>
              <input style={inpRO} value={form.noRef || ""} readOnly
                     data-testid="homolog-noref" />
              <label style={lbl}>Avocat</label>
              <input style={inpRO} readOnly data-testid="homolog-avocat"
                     value={`${form.NoAvo || ""} ${form.NomAvo || ""} ${form.PrenomAvo || ""}`.trim()} />
            </div>
          </div>

          {/* Requérant + Corequérant côte à côte */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                        gap: 8, marginTop: 8 }}>
            {/* --- Requérant --- */}
            <div style={fs}>
              <div style={sectionTitle}>Requérant</div>
              <div style={fsBody}>
                {[
                  ["Nom", "nom_req"],
                  ["Prénom", "prenom_req"],
                  ["Date naissance", "dateNaiss_req"],
                  ["Région / Bureau", null],
                  ["No. séquence", "noSeq_req"],
                  ["Nature", "natures_req"],
                ].map(([label, key]) => key === null ? (
                  <div key="reg-bur" style={row}>
                    <label style={lbl}>{label}</label>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                      <input style={inpRO} readOnly
                             value={
                               form.noReg_req
                                 ? `${form.noReg_req}${(regions.find(
                                     r => r.code === form.noReg_req)?.description
                                     ? ` - ${regions.find(r => r.code === form.noReg_req).description}` : "")}`
                                 : ""}
                             data-testid="homolog-noReg_req" />
                      <input style={inpRO} readOnly
                             value={
                               form.noBur_req
                                 ? `${form.noBur_req}${(bureauxReq.find(
                                     b => b.code === form.noBur_req)?.bureau
                                     ? ` - ${bureauxReq.find(b => b.code === form.noBur_req).bureau}` : "")}`
                                 : ""}
                             data-testid="homolog-noBur_req" />
                    </div>
                  </div>
                ) : (
                  <div key={key} style={row}>
                    <label style={lbl}>{label}</label>
                    <input style={inpRO} value={form[key] || ""} readOnly
                           data-testid={`homolog-${key}`} />
                  </div>
                ))}
                {/* Émetteur + Options en 1 ligne */}
                <div style={{ marginTop: 4, padding: 4, background: "#eaf1f8",
                              border: "1px solid #b8ccd8", display: "flex",
                              gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <label style={chkLbl}>
                    <input type="radio" name="emetteur_req"
                           checked={form.emetteurAttest_req === "1"}
                           onChange={() => {
                             setField("emetteurAttest_req", "1");
                             setField("emetteurAttest_req2", "0");
                           }} data-testid="homolog-emet-req" />
                    <b>Bureau émetteur</b>
                  </label>
                  <label style={chkLbl}>
                    <input type="checkbox" checked={form.renoncement_req === "1"}
                           onChange={e => setFlag("renoncement_req", e.target.checked)}
                           data-testid="homolog-renonce-req" />
                    Renonciation
                  </label>
                  <label style={{ ...chkLbl, color: "#c00", fontWeight: 700 }}>
                    <input type="checkbox" checked={form.formRequis_req === "1"}
                           onChange={e => setFlag("formRequis_req", e.target.checked)}
                           data-testid="homolog-form-req" />
                    Formulaire requis *
                  </label>
                </div>
                {/* Refus Requérant — compact */}
                <div style={{ marginTop: 4, border: "1px solid #f43f5e",
                              background: "#fff5f5", padding: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#c00",
                                marginBottom: 2 }}>Refus</div>
                  {[
                    ["refus47_req", "Art. 4.7(1.1) - Non conforme"],
                    ["refus42_req", "Art. 4.7(1.1) - Absente 60j"],
                    ["retrait_req", "Art. 4.11.1(1) - Retrait / Impossible d{'\u2019'}entendre"],
                  ].map(([k, txt]) => (
                    <label key={k} style={{ ...chkLbl, display: "block", fontSize: 12 }}>
                      <input type="checkbox" checked={form[k] === "1"}
                             onChange={e => setFlag(k, e.target.checked)}
                             data-testid={`homolog-${k.replace("_", "-")}`} />
                      {" "}{txt.replace("d{'\u2019'}", "d\u2019")}
                    </label>
                  ))}
                </div>
                <button disabled title="Upload PDF — Phase 3"
                        data-testid="homolog-lier-formulaire-req"
                        style={{ marginTop: 4, background: "#7ba9c0", color: "#fff",
                                 border: "1px solid #555", padding: "3px 8px",
                                 fontSize: 12, width: "100%", opacity: 0.6 }}>
                  Lier formulaire requis (Phase 3)
                </button>
              </div>
            </div>

            {/* --- Corequérant --- */}
            <div style={fs}>
              <div style={sectionTitle}>Corequérant</div>
              <div style={fsBody}>
                <div style={row}>
                  <label style={lbl}>Nom <span style={{ color: "#c00" }}>*</span></label>
                  <input style={noSeqReq2Set ? inpRO : inp}
                         value={form.nom_req2 || ""} readOnly={noSeqReq2Set} maxLength={40}
                         onChange={e => setField("nom_req2", e.target.value)}
                         data-testid="homolog-nom-req2" />
                </div>
                <div style={row}>
                  <label style={lbl}>Prénom <span style={{ color: "#c00" }}>*</span></label>
                  <input style={noSeqReq2Set ? inpRO : inp}
                         value={form.prenom_req2 || ""} readOnly={noSeqReq2Set} maxLength={40}
                         onChange={e => setField("prenom_req2", e.target.value)}
                         data-testid="homolog-prenom-req2" />
                </div>
                <div style={row}>
                  <label style={lbl}>Date naiss. <span style={{ color: "#c00" }}>*</span></label>
                  <DateInput value={form.dateNaiss_req2 || ""}
                             onChange={v => setField("dateNaiss_req2", v || "")}
                             disabled={noSeqReq2Set}
                             style={noSeqReq2Set ? inpRO : inp}
                             testId="homolog-datenaiss-req2" />
                </div>
                <div style={row}>
                  <label style={lbl}>Région / Bureau <span style={{ color: "#c00" }}>*</span></label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
                    {noSeqReq2Set ? (
                      <>
                        {/* Verrouillé après Lier — affiche code + description */}
                        <input style={inpRO} readOnly
                               value={
                                 form.noReg_req2
                                   ? `${form.noReg_req2} - ${(regions.find(
                                       r => r.code === form.noReg_req2)?.description) || ""}`.trim()
                                   : ""}
                               data-testid="homolog-reg-req2" />
                        <input style={inpRO} readOnly
                               value={
                                 form.noBur_req2
                                   ? `${form.noBur_req2} - ${(bureauxByRegion.find(
                                       b => b.code === form.noBur_req2)?.bureau) || ""}`.trim()
                                   : ""}
                               data-testid="homolog-bur-req2" />
                      </>
                    ) : (
                      <>
                        <select style={inp}
                                value={form.noReg_req2 || ""}
                                onChange={e => {
                                  setField("noReg_req2", e.target.value);
                                  setField("noBur_req2", "");
                                }} data-testid="homolog-reg-req2">
                          <option value=""></option>
                          {regions.map(r => (
                            <option key={r.code} value={r.code}>
                              {r.code}{r.description ? ` - ${r.description}` : ""}
                            </option>
                          ))}
                        </select>
                        <select style={inp}
                                value={form.noBur_req2 || ""}
                                disabled={!form.noReg_req2}
                                onChange={e => setField("noBur_req2", e.target.value)}
                                data-testid="homolog-bur-req2">
                          <option value=""></option>
                          {bureauxByRegion.map(b => (
                            <option key={b.code} value={b.code}>
                              {b.code} - {b.bureau}
                            </option>
                          ))}
                        </select>
                      </>
                    )}
                  </div>
                </div>
                <div style={row}>
                  <label style={lbl}>No. séquence</label>
                  <input style={inpRO} value={form.noSeq_req2 || ""} readOnly
                         data-testid="homolog-noseq-req2" />
                </div>
                <div style={row}>
                  <label style={lbl}>Nature</label>
                  <input style={inpRO} value={form.natures_req2 || ""} readOnly
                         data-testid="homolog-nature-req2" />
                </div>
                <div style={{ marginTop: 4, padding: 4, background: "#eaf1f8",
                              border: "1px solid #b8ccd8", display: "flex",
                              gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                  <label style={chkLbl}>
                    <input type="radio" name="emetteur_req"
                           checked={form.emetteurAttest_req2 === "1"}
                           onChange={() => {
                             setField("emetteurAttest_req2", "1");
                             setField("emetteurAttest_req", "0");
                           }} data-testid="homolog-emet-req2" />
                    <b>Bureau émetteur</b>
                  </label>
                  <label style={chkLbl}>
                    <input type="checkbox" checked={form.renoncement_req2 === "1"}
                           onChange={e => setFlag("renoncement_req2", e.target.checked)}
                           data-testid="homolog-renonce-req2" />
                    Renonciation
                  </label>
                  <label style={chkLbl}>
                    <input type="checkbox" checked={form.formRequis_req2 === "1"}
                           onChange={e => setFlag("formRequis_req2", e.target.checked)}
                           data-testid="homolog-form-req2" />
                    Formulaire requis
                  </label>
                </div>
                <div style={{ marginTop: 4, border: "1px solid #f43f5e",
                              background: "#fff5f5", padding: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#c00",
                                marginBottom: 2 }}>Refus</div>
                  {[
                    ["refus47_req2", "Art. 4.7(1.1) - Non conforme"],
                    ["refus42_req2", "Art. 4.7(1.1) - Absente 60j"],
                    ["retrait_req2", "Art. 4.11.1(1) - Retrait / Impossible d\u2019entendre"],
                  ].map(([k, txt]) => (
                    <label key={k} style={{ ...chkLbl, display: "block", fontSize: 12 }}>
                      <input type="checkbox" checked={form[k] === "1"}
                             onChange={e => setFlag(k, e.target.checked)}
                             data-testid={`homolog-${k.replace("_", "-")}`} />
                      {" "}{txt}
                    </label>
                  ))}
                </div>
                <button disabled title="Upload PDF — Phase 3"
                        data-testid="homolog-lier-formulaire-req2"
                        style={{ marginTop: 4, background: "#7ba9c0", color: "#fff",
                                 border: "1px solid #555", padding: "3px 8px",
                                 fontSize: 12, width: "100%", opacity: 0.6 }}>
                  Lier formulaire requis (Phase 3)
                </button>
              </div>
            </div>
          </div>

          {/* Paiement Req + Info paiement Coreq — dernière ligne */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr",
                        gap: 8, marginTop: 8 }}>
            <div style={fs}>
              <div style={sectionTitle}>Paiement (Requérant)</div>
              <div style={{ ...fsBody, display: "grid",
                            gridTemplateColumns: "90px 1fr 90px 1fr",
                            gap: 4, alignItems: "center" }}>
                <label style={lbl}>Date paiement</label>
                <DateInput value={form.date_paiement || ""}
                           onChange={v => setField("date_paiement", v || "")}
                           disabled={form.gratuit_req === "1"}
                           style={form.gratuit_req === "1" ? inpRO : inp}
                           testId="homolog-date-paiement" />
                <label style={lbl}>Type paiement</label>
                <select style={form.gratuit_req === "1" ? inpRO : inp}
                        value={form.type_paiement || ""}
                        disabled={form.gratuit_req === "1"}
                        onChange={e => setField("type_paiement", e.target.value)}
                        data-testid="homolog-type-paiement">
                  <option value=""></option>
                  {typesPaiement.map(t => (
                    <option key={t.code} value={t.code}>{t.code}</option>
                  ))}
                </select>
                <label style={lbl}>No. paiement</label>
                <input style={form.gratuit_req === "1" ? inpRO : inp}
                       value={form.NoPaiement || ""}
                       disabled={form.gratuit_req === "1"}
                       onChange={e => setField("NoPaiement", e.target.value)}
                       data-testid="homolog-no-paiement" />
                <label style={lbl}>Montant</label>
                <input style={{ ...inpRO, fontWeight: 700, textAlign: "right" }}
                       value={`${(parseFloat(form.montant) || 0).toFixed(2)} $`}
                       readOnly data-testid="homolog-montant" />
                <div style={{ gridColumn: "1 / span 4", display: "flex", gap: 20,
                              marginTop: 2 }}>
                  <label style={{ ...chkLbl, fontWeight: 700 }}>
                    <input type="checkbox" checked={form.gratuit_req === "1"}
                           onChange={e => setFlag("gratuit_req", e.target.checked)}
                           data-testid="homolog-gratuit-req" />
                    Gratuit
                  </label>
                  <label style={{ ...chkLbl, fontWeight: 700 }}>
                    <input type="checkbox" checked={form.paye_req === "1"}
                           disabled={form.gratuit_req === "1"}
                           onChange={e => setFlag("paye_req", e.target.checked)}
                           data-testid="homolog-paye-req" />
                    Payé
                  </label>
                </div>
              </div>
            </div>
            <div style={fs}>
              <div style={sectionTitle}>Info paiement (Corequérant)</div>
              <div style={{ ...fsBody, display: "grid",
                            gridTemplateColumns: "110px 1fr", gap: 4,
                            alignItems: "center" }}>
                <label style={lbl}>Courriel bureau</label>
                <input style={inpRO} value={form.EmailBurReq2 || ""} readOnly
                       data-testid="homolog-email-req2" />
                <label style={lbl}>No. dossier cour</label>
                <input style={inpRO} value={form.NoDossierCour || ""} readOnly
                       data-testid="homolog-nodossiercour" />
                <div style={{ gridColumn: "1 / span 2", display: "flex", gap: 20,
                              marginTop: 2 }}>
                  <label style={{ ...chkLbl, fontWeight: 700 }}>
                    <input type="checkbox" checked={form.gratuit_req2 === "1"}
                           disabled data-testid="homolog-gratuit-req2" />
                    Gratuit
                  </label>
                  <label style={{ ...chkLbl, fontWeight: 700 }}>
                    <input type="checkbox" checked={form.paye_req2 === "1"}
                           disabled data-testid="homolog-paye-req2" />
                    Payé
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HomologOrphelinesModal
        isOpen={showOrphelines}
        nobur={(form.noBur_req || "").substring(0, 2)}
        currentReq={{
          noReg: form.noReg_req,
          noBur: form.noBur_req,
          noSeq: form.noSeq_req,
          nom: form.nom_req, prenom: form.prenom_req,
          dateNaiss: form.dateNaiss_req,
          natures: form.natures_req,
        }}
        onLier={async (linked) => {
          // Après Lier : populer le bloc Corequérant avec l'info du co-req de
          // la ligne orpheline (parité `RecupInfoGrid` FrmListHomolog.vb:85-104).
          //
          // Cas "même bureau" (parité UpdatLignOrpheline:127-131) : si le
          // requérant et le co-req sont dans le même bureau, on remplace les
          // infos req2 par celles du requérant ORIGINAL de la ligne orpheline
          // (nom_req, prenom_req, dateNaiss_req) — infos saisies par la
          // personne elle-même, donc plus sûres que les infos req2.
          const sameBureau = (linked.noReg_req2 === linked.noReg_req)
                             && (linked.noBur_req2 === linked.noBur_req);
          const nomR2  = sameBureau ? linked.nom_req      : linked.nom_req2;
          const prR2   = sameBureau ? linked.prenom_req   : linked.prenom_req2;
          const dnR2   = sameBureau ? linked.dateNaiss_req: linked.dateNaiss_req2;
          const natR2  = sameBureau ? linked.natures_req  : linked.natures_req2;

          // Pré-charger les bureaux de la région du co-req pour que le
          // dropdown/readonly display affiche le libellé complet.
          const newRegReq2 = linked.noReg_req2 || "";
          if (newRegReq2) {
            try {
              const { data } = await axios.get(
                `${API}/reference/bureaux-by-region`,
                { params: { region: newRegReq2.substring(0, 2) } });
              setBureauxByRegion(data || []);
            } catch (e) { /* silencieux */ }
          }

          setForm(p => ({ ..._normalizeFlags(p),
            nom_req2:      nomR2 || p.nom_req2,
            prenom_req2:   prR2  || p.prenom_req2,
            dateNaiss_req2: dnR2 || p.dateNaiss_req2,
            noReg_req2:    linked.noReg_req2 || p.noReg_req2,
            noBur_req2:    linked.noBur_req2 || p.noBur_req2,
            noSeq_req2:    linked.noSeq_req2 || p.noSeq_req2,
            natures_req2:  natR2 || p.natures_req2,
            formRequis_req2: _normalizeFlags(linked).formRequis_req2 || p.formRequis_req2,
            renoncement_req2: _normalizeFlags(linked).renoncement_req2 || p.renoncement_req2,
            emetteurAttest_req:  _normalizeFlags(linked).emetteurAttest_req  === "1" ? "1" : p.emetteurAttest_req,
            emetteurAttest_req2: _normalizeFlags(linked).emetteurAttest_req2 === "1" ? "1" : p.emetteurAttest_req2,
            homolog_id:    linked.homolog_id || p.homolog_id,
          }));
          setRowId(linked.row_id || null);
          setIsDirty(true);
        }}
        onClose={() => setShowOrphelines(false)}
        showPopup={showPopup}
      />
    </div>
  );
};

export default HomologModal;
